import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, Loader2, Music, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const SAMPLES = [
  "Liam Smith live at Magnums this Friday 8pm. Free entry, acoustic set.",
  "Trivia Tuesday at Boaty's — every Tuesday 7pm. $5 schooners, prizes for the top 3 teams.",
  "Sunday sessions with DJ Reefer at the Garden Bar, every Sunday from 4pm. No cover.",
];

export default function QuickGig() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const parseAndContinue = async () => {
    if (!text.trim()) {
      toast.error("Paste something first 🙂");
      return;
    }
    setParsing(true);
    try {
      const r = await api.post("/events/parse", { text });
      // Encode the parsed payload as base64 JSON and hand to SubmitEvent via query string
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(r.data))));
      navigate(`/dashboard/submit-event?prefill=${encoded}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI parse failed";
      toast.error(msg);
    } finally {
      setParsing(false);
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Music className="w-7 h-7 text-coastal" />
          <h1 className="font-heading text-4xl text-navy tracking-tight" data-testid="quick-gig-title">
            Quick add a gig
          </h1>
        </div>
        <p className="text-neutral-500 mb-8">
          Paste a Facebook caption, Instagram post or just a quick sentence. AI fills in the venue, time and details — you review and submit.
        </p>

        <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-5">
          <div className="relative">
            <Sparkles className="absolute left-4 top-4 w-5 h-5 text-coastal pointer-events-none" />
            <Textarea
              rows={6}
              placeholder="e.g.  Liam Smith live at Magnums Friday 8pm — free entry, acoustic set"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="pl-12 pr-4 text-base"
              maxLength={4000}
              data-testid="quick-gig-textarea"
            />
            <div className="text-xs text-neutral-400 mt-1 text-right">{text.length}/4000</div>
          </div>

          <Button
            onClick={parseAndContinue}
            disabled={parsing || !text.trim()}
            className="w-full rounded-full bg-navy hover:bg-navy/90 text-base h-12"
            data-testid="quick-gig-parse-btn"
          >
            {parsing ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Parsing with AI…</>
            ) : (
              <>Parse & review <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>

          <div className="pt-3 border-t border-black/5">
            <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2">Or try one of these</div>
            <div className="space-y-2">
              {SAMPLES.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setText(s)}
                  className="block text-left w-full text-sm text-neutral-600 hover:text-navy bg-sand/30 hover:bg-sand/50 rounded-lg px-4 py-2 transition-colors"
                  data-testid={`quick-gig-sample-${i}`}
                >
                  “{s}”
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 text-sm text-neutral-500 text-center">
          Prefer the full form? <button onClick={() => navigate("/dashboard/submit-event")} className="text-navy underline underline-offset-4 hover:no-underline">Submit manually</button>
        </div>
      </div>
    </Layout>
  );
}
